import { useQuery } from '@apollo/client';
import { Campaign, CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import { Autocomplete, Box, Checkbox, Stack, TextField } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { parseUrlParam } from '~/components/Filter';
import { ListPagination } from '~/components/ListPagination';
import { ListContainer, ListContent, ListPageContainer } from '~/components/PageLayout';
import { AnnouncementCard } from '~/components/announcements/AnnouncementCard';
import { SEVERITY_FILTER, useSeverityLabels } from '~/components/announcements/SeverityChip';
import { useMarkAnnouncementsRead } from '~/components/announcements/mark-read';
import { EmptyState } from '~/components/empty-state';
import { LoadingPane } from '~/components/loading-pane';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import type { AnnouncementCategory, AnnouncementSeverity } from '~/generated/graphql';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import AnnouncementsIllustration from '~/images/illustrations/announcements.svg';
import { AnnouncementFiltersDocument, AnnouncementListDocument } from './queries/list.generated';

const READ_OPTIONS = ['all', 'unread', 'read'] as const;

const READ_FILTER = z.enum(READ_OPTIONS).catch('all');

// The four share the row rather than each taking a fixed width, so they stay on one line at
// the container's 760px and wrap only when it is genuinely too narrow. The 40px control keeps
// a selection from growing the field.
const FILTER_SX = {
  flex: '1 1 150px',
  '& .MuiOutlinedInput-root': { height: 40, paddingBottom: 0, paddingTop: 0 },
};

const TAGS_SCHEMA = z.array(z.string());

const SEVERITIES_SCHEMA = z.array(z.enum(['CRITICAL', 'WARN', 'INFO']));

const CATEGORY_OPTIONS = [
  'CMS_RELEASE',
  'DEVICE_SOFTWARE',
  'CONTENT',
  'MAINTENANCE',
  'BILLING',
] as const;

const CATEGORIES_SCHEMA = z.array(z.enum(CATEGORY_OPTIONS));

// The enum name is not the column value the API counts under, so the mapping is written down
// rather than assumed from the casing.
const CATEGORY_FILTER = {
  BILLING: 'billing',
  CMS_RELEASE: 'cms_release',
  CONTENT: 'content',
  DEVICE_SOFTWARE: 'device_software',
  MAINTENANCE: 'maintenance',
} as const satisfies Record<AnnouncementCategory, string>;

export const AnnouncementList = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const [markRead] = useMarkAnnouncementsRead();

  const { page, perPage, handleOutOfBounds } = usePaginationParams();

  const [searchParams, setSearchParams] = useSearchParams();
  const readFilter = READ_FILTER.parse(searchParams.get('read'));
  const tags = useMemo(
    () =>
      parseUrlParam(() => TAGS_SCHEMA.parse(JSON.parse(searchParams.get('tags') || '[]')), 'tags'),
    [searchParams],
  );

  const severities = useMemo(
    () =>
      parseUrlParam(
        () => SEVERITIES_SCHEMA.parse(JSON.parse(searchParams.get('severities') || '[]')),
        'severities',
      ),
    [searchParams],
  );

  const categories = useMemo(
    () =>
      parseUrlParam(
        () => CATEGORIES_SCHEMA.parse(JSON.parse(searchParams.get('categories') || '[]')),
        'categories',
      ),
    [searchParams],
  );

  const setFilter = useCallback(
    (key: 'categories' | 'read' | 'severities' | 'tags', value: string | undefined) => {
      setSearchParams((current) => {
        const params = Object.fromEntries(current);
        if (value === undefined) delete params[key];
        else params[key] = value;
        // A filter reshapes the result set, so page 1 is the only page guaranteed to exist.
        return { ...params, page: '1' };
      });
    },
    [setSearchParams],
  );

  const read = readFilter === 'all' ? null : readFilter === 'read';

  // Filtered in the database, so the pager counts what the list actually shows.
  const { data, loading } = useQuery(AnnouncementListDocument, {
    variables: { categories, page, perPage, read, severities, tags },
    onCompleted: (data) => handleOutOfBounds(data.announcements.pageInfo),
  });

  // Its own query so options and counts cover everything visible, not just the loaded page.
  const { data: filterData } = useQuery(AnnouncementFiltersDocument, {
    variables: { categories, read, severities, tags },
  });
  const tagOptions = useMemo(
    () => filterData?.announcementFilterInfo.tags.map((tag) => tag.filter) ?? [],
    [filterData],
  );

  // Spelled out rather than built from the value, so i18n-tasks can see the keys are used.
  const readLabels: Record<(typeof READ_OPTIONS)[number], string> = useMemo(
    () => ({
      all: t('components.announcements.filter.all'),
      read: t('components.announcements.filter.read'),
      unread: t('components.announcements.filter.unread'),
    }),
    [t],
  );

  const readCount = useCallback(
    (filter: string) =>
      filterData?.announcementFilterInfo.read.find((c) => c.filter === filter)?.count ?? 0,
    [filterData],
  );

  const severityLabels = useSeverityLabels();

  const severityCount = useCallback(
    (severity: AnnouncementSeverity) =>
      filterData?.announcementFilterInfo.severities.find(
        (count) => count.filter === SEVERITY_FILTER[severity],
      )?.count ?? 0,
    [filterData],
  );

  // Spelled out rather than built from the value, so i18n-tasks can see the keys are used.
  const categoryLabels: Record<AnnouncementCategory, string> = useMemo(
    () => ({
      BILLING: t('components.announcements.category.billing'),
      CMS_RELEASE: t('components.announcements.category.cms_release'),
      CONTENT: t('components.announcements.category.content'),
      DEVICE_SOFTWARE: t('components.announcements.category.device_software'),
      MAINTENANCE: t('components.announcements.category.maintenance'),
    }),
    [t],
  );

  const categoryCount = useCallback(
    (category: AnnouncementCategory) =>
      filterData?.announcementFilterInfo.categories.find(
        (count) => count.filter === CATEGORY_FILTER[category],
      )?.count ?? 0,
    [filterData],
  );

  const tagCount = useCallback(
    (tag: string) =>
      filterData?.announcementFilterInfo.tags.find((count) => count.filter === tag)?.count ?? 0,
    [filterData],
  );

  const announcements = useMemo(() => data?.announcements.nodes ?? [], [data?.announcements]);
  const totalCount = useTotalCount(data?.announcements.pageInfo.nodesCount);

  return (
    <LoadingPane in={loading} size={80} thickness={4}>
      <>
        <Toolbar titleIcon={<Campaign />} titleText={t('components.announcements.title')} />
        <ListPageContainer>
          <ListContainer>
            <ListContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                  <Box sx={{ maxWidth: 760, mx: 'auto', my: 3 }}>
                    <Stack
                      direction="row"
                      sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}
                    >
                      {/* Single select: the three states are exclusive, and "All" is the
                          unfiltered default rather than a fourth option. */}
                      <Autocomplete
                        disableClearable
                        options={READ_OPTIONS}
                        value={readFilter}
                        getOptionLabel={(option) => readLabels[option]}
                        onChange={(_, value) =>
                          setFilter('read', value === 'all' ? undefined : value)
                        }
                        renderOption={(props, option) => (
                          <Box component="li" {...props} key={option}>
                            {readLabels[option]} ({readCount(option)})
                          </Box>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t('components.announcements.filter.status')}
                            size="small"
                          />
                        )}
                        sx={FILTER_SX}
                      />

                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        limitTags={1}
                        options={CATEGORY_OPTIONS}
                        value={categories}
                        getOptionLabel={(option) => categoryLabels[option]}
                        onChange={(_, value) =>
                          setFilter(
                            'categories',
                            value.length > 0 ? JSON.stringify(value) : undefined,
                          )
                        }
                        renderOption={(props, option, { selected }) => (
                          <Box component="li" {...props} key={option}>
                            <Checkbox
                              icon={<CheckBoxOutlineBlank fontSize="small" />}
                              checkedIcon={<CheckBox fontSize="small" />}
                              style={{ marginRight: 8 }}
                              checked={selected}
                            />
                            {categoryLabels[option]} ({categoryCount(option)})
                          </Box>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t('components.announcements.filter.category')}
                            size="small"
                          />
                        )}
                        ChipProps={{ size: 'small' }}
                        sx={FILTER_SX}
                      />

                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        limitTags={1}
                        options={['CRITICAL', 'WARN', 'INFO'] as const}
                        value={severities}
                        getOptionLabel={(option) => severityLabels[option]}
                        onChange={(_, value) =>
                          setFilter(
                            'severities',
                            value.length > 0 ? JSON.stringify(value) : undefined,
                          )
                        }
                        renderOption={(props, option, { selected }) => (
                          <Box component="li" {...props} key={option}>
                            <Checkbox
                              icon={<CheckBoxOutlineBlank fontSize="small" />}
                              checkedIcon={<CheckBox fontSize="small" />}
                              style={{ marginRight: 8 }}
                              checked={selected}
                            />
                            {severityLabels[option]} ({severityCount(option)})
                          </Box>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t('components.announcements.filter.severity')}
                            size="small"
                          />
                        )}
                        ChipProps={{ size: 'small' }}
                        sx={FILTER_SX}
                      />

                      <Autocomplete
                        multiple
                        // Or the menu closes after every tag and you reopen it to pick a second.
                        disableCloseOnSelect
                        // Collapses the rest to "+N" so the field cannot grow past its width.
                        limitTags={1}
                        options={tagOptions}
                        value={tags}
                        onChange={(_, value) =>
                          setFilter('tags', value.length > 0 ? JSON.stringify(value) : undefined)
                        }
                        renderOption={(props, option, { selected }) => (
                          <Box component="li" {...props} key={option}>
                            <Checkbox
                              icon={<CheckBoxOutlineBlank fontSize="small" />}
                              checkedIcon={<CheckBox fontSize="small" />}
                              style={{ marginRight: 8 }}
                              checked={selected}
                            />
                            {option} ({tagCount(option)})
                          </Box>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t('components.announcements.filter.tag')}
                            size="small"
                          />
                        )}
                        ChipProps={{ size: 'small' }}
                        sx={FILTER_SX}
                      />
                    </Stack>

                    {/* Waits for a response — an unanswered query is not the same as nothing
                        to show, and changing a filter briefly leaves it unanswered. */}
                    {data && announcements.length === 0 && (
                      <EmptyState
                        illustration={AnnouncementsIllustration}
                        header={t('components.announcements.empty_state.header')}
                        description={t('components.announcements.empty_state.description')}
                      />
                    )}

                    {announcements.map((announcement) => (
                      <AnnouncementCard
                        key={announcement.id}
                        announcement={announcement}
                        clip
                        href={`/networks/${currentNetwork.id}/announcements/${announcement.id}`}
                        onOpen={() => {
                          if (announcement.read) return;
                          void markRead({
                            variables: {
                              input: { announcementIds: [announcement.id], read: true },
                            },
                          });
                        }}
                      />
                    ))}
                  </Box>
                </Box>

                {/* Same column as the cards, or it right-aligns against the full page width
                    and floats away from the content. */}
                <Box sx={{ flexShrink: 0, maxWidth: 760, mx: 'auto', width: '100%' }}>
                  <ListPagination totalCount={totalCount} />
                </Box>
              </Box>
            </ListContent>
          </ListContainer>
        </ListPageContainer>
      </>
    </LoadingPane>
  );
};
